import { education, experience, pick, projects, skills, volunteer } from '@/data/cv';
import PageMeta from '@/shared/components/PageMeta';
import AcademicLayout from '@/shared/components/AcademicLayout';
import { useLanguage } from '@/shared/contexts/LanguageContext';

interface CVSectionProps {
  number: string;
  title: string;
  accent: 'blue' | 'purple' | 'green' | 'orange' | 'cyan';
  children: React.ReactNode;
}

const accentStyles = {
  blue: {
    line: 'from-blue-500/50 via-blue-500/20',
    number: 'text-blue-500/70',
    text: 'text-blue-600 dark:text-blue-400'
  },
  purple: {
    line: 'from-purple-500/50 via-purple-500/20',
    number: 'text-purple-500/70',
    text: 'text-purple-600 dark:text-purple-400'
  },
  green: {
    line: 'from-green-500/50 via-green-500/20',
    number: 'text-green-500/70',
    text: 'text-green-600 dark:text-green-400'
  },
  orange: {
    line: 'from-orange-500/50 via-orange-500/20',
    number: 'text-orange-500/70',
    text: 'text-orange-600 dark:text-orange-400'
  },
  cyan: {
    line: 'from-cyan-500/50 via-cyan-500/20',
    number: 'text-cyan-500/70',
    text: 'text-cyan-600 dark:text-cyan-400'
  }
} as const;

function CVSection({ number, title, accent, children }: CVSectionProps) {
  const styles = accentStyles[accent];
  return (
    <section className="relative">
      <div
        className={`absolute -left-12 lg:left-10 top-0 h-full w-0.5 bg-gradient-to-b ${styles.line} to-transparent`}
      ></div>
      <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] gap-4 md:gap-8">
        <div className="md:text-right">
          <h2 className="text-2xl font-light text-foreground flex items-center md:justify-end gap-3">
            <span className={`text-4xl font-thin ${styles.number}`}>{number}</span>
          </h2>
        </div>
        <div>
          <h2 className="text-2xl font-light text-foreground mb-10">{title}</h2>
          {children}
        </div>
      </div>
    </section>
  );
}

interface CVItemProps {
  period: string;
  periodClass: string;
  location?: string;
  children: React.ReactNode;
}

function CVItem({ period, periodClass, location, children }: CVItemProps) {
  return (
    <article className="grid grid-cols-1 md:grid-cols-[160px_1fr] gap-4 md:gap-8">
      <div className="md:text-right">
        {period && <p className={`text-sm font-medium ${periodClass}`}>{period}</p>}
        {location && <p className="text-xs text-muted-foreground mt-1">{location}</p>}
      </div>
      <div className="space-y-2">{children}</div>
    </article>
  );
}

export default function CVPage() {
  const { language, t } = useLanguage();

  return (
    <AcademicLayout>
      <PageMeta title="CV" description="Curriculum vitae of Kaibin Zhang — education, experience, projects and skills." path="/cv" />
      <div className="relative pl-4 lg:pl-0">
        <div className="space-y-24">
          <CVSection number="01" title={t('education')} accent="blue">
            <div className="space-y-6">
              {education.map((edu, index) => (
                <CVItem key={index} period={pick(edu.year, language)} periodClass={accentStyles.blue.text}>
                  <h3 className="text-lg font-medium text-foreground">
                    {pick(edu.degree, language)} · {pick(edu.field, language)}
                  </h3>
                  <p className="text-base text-muted-foreground">{pick(edu.institution, language)}</p>
                  <p className="text-sm text-muted-foreground/80 leading-relaxed">
                    {pick(edu.description, language)}
                  </p>
                </CVItem>
              ))}
            </div>
          </CVSection>

          <CVSection number="02" title={t('workExperience')} accent="purple">
            <div className="space-y-10">
              {experience.map((exp, index) => (
                <CVItem
                  key={index}
                  period={pick(exp.period, language)}
                  periodClass={accentStyles.purple.text}
                  location={exp.location ? pick(exp.location, language) : undefined}
                >
                  <h3 className="text-lg font-medium text-foreground">{pick(exp.title, language)}</h3>
                  <p className="text-base text-muted-foreground">{pick(exp.company, language)}</p>
                  <div className="space-y-1.5 mt-3">
                    {exp.description.map((desc, descIndex) => (
                      <p key={descIndex} className="text-sm text-muted-foreground/80 leading-relaxed">
                        {pick(desc, language)}
                      </p>
                    ))}
                  </div>
                </CVItem>
              ))}
            </div>
          </CVSection>

          <CVSection number="03" title={t('cvProjects')} accent="green">
            <div className="space-y-8">
              {projects.map((proj, index) => (
                <CVItem key={index} period={pick(proj.period, language)} periodClass={accentStyles.green.text}>
                  <h3 className="text-lg font-medium text-foreground">{pick(proj.title, language)}</h3>
                  <div className="space-y-1.5">
                    {proj.description.map((desc, descIndex) => (
                      <p key={descIndex} className="text-sm text-muted-foreground/80 leading-relaxed">
                        {pick(desc, language)}
                      </p>
                    ))}
                  </div>
                </CVItem>
              ))}
            </div>
          </CVSection>

          <CVSection number="04" title={t('volunteerExperience')} accent="orange">
            <div className="space-y-8">
              {volunteer.map((vol, index) => (
                <CVItem
                  key={index}
                  period={vol.period ? pick(vol.period, language) : ''}
                  periodClass={accentStyles.orange.text}
                >
                  <h3 className="text-lg font-medium text-foreground">{pick(vol.title, language)}</h3>
                  <p className="text-base text-muted-foreground">{pick(vol.organization, language)}</p>
                  <div className="space-y-1.5 mt-3">
                    {vol.description.map((desc, descIndex) => (
                      <p key={descIndex} className="text-sm text-muted-foreground/80 leading-relaxed">
                        {pick(desc, language)}
                      </p>
                    ))}
                  </div>
                </CVItem>
              ))}
            </div>
          </CVSection>

          <CVSection number="05" title={t('cvSkills')} accent="cyan">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {skills.map((skill, index) => (
                <div key={index} className="space-y-3">
                  <h3 className={`text-sm font-medium ${accentStyles.cyan.text} uppercase tracking-wider`}>
                    {pick(skill.category, language)}
                  </h3>
                  <ul className="space-y-1.5">
                    {skill.items.map((item, itemIndex) => (
                      <li
                        key={itemIndex}
                        className="text-sm text-muted-foreground/80 flex items-start gap-2"
                      >
                        <span className="w-1 h-1 bg-cyan-500/40 rounded-full mt-1.5 flex-shrink-0"></span>
                        <span>{pick(item, language)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </CVSection>
        </div>
      </div>
    </AcademicLayout>
  );
}
